import { fetchItemsInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { LocalCombinationItem } from "@coworker/apprestructured/src/shared/types/localCombinationItem";
import { Article, SyncArticle } from "../types/article";

export function diffCombinations(
  latestCombinationArticlesData: Item[],
  combinationArticlesCollection: Article[],
  articlesInCombination: Article[]
) {
  const toAdd: SyncArticle[] = [];
  const toUpdate: Article[] = [];
  const toDelete: { id: string }[] = [];
  const processedCombinations = new Set<string>();

  for (const combination of latestCombinationArticlesData as LocalCombinationItem[]) {
    if (processedCombinations.has(combination.no)) continue;
    processedCombinations.add(combination.no);

    const parentArticles = combinationArticlesCollection.filter(
      (article) => article.productArticleId === combination.no
    );

    if (parentArticles.length === 0) continue;

    for (const parentArticle of parentArticles) {
      const parentId = parentArticle.productArticleId;
      const latestItems = combination.itemsInCombination ?? [];
      const parentGroupId = parentArticle.groupId ?? "";

      const currentChildren = articlesInCombination.filter(
        (article) =>
          article.parentArticleCombinationNumber === parentId &&
          (article.groupId ?? "") === parentGroupId
      );

      for (const item of latestItems) {
        const matchingChild = currentChildren.find(
          (article) => article.productArticleId === item.item_no
        );

        if (!matchingChild) {
          toAdd.push({
            parentId,
            parentQty: parentArticle.nbrArticles || 1,
            itemNo: item.item_no,
            quantity: item.quantity,
            mediaId: parentArticle.mediaId || "",
            articlesCountInCombination: item.quantity,
            groupId: parentGroupId,
            groupName: parentArticle.groupName ?? "",
          });
          continue;
        }

        if (
          Number(matchingChild.articlesCountInCombination) !== item.quantity
        ) {
          toUpdate.push({
            ...matchingChild,
            groupId: matchingChild.groupId ?? "",
            groupName: matchingChild.groupName ?? "",
            articlesCountInCombination: item.quantity,
          });
        }
      }

      const latestItemIds = new Set(latestItems.map((item) => item.item_no));
      for (const currentChild of currentChildren) {
        if (
          !latestItemIds.has(currentChild.productArticleId) &&
          currentChild.id
        ) {
          toDelete.push({ id: currentChild.id });
        }
      }

      if (
        Number(parentArticle.articlesCountInCombination) !== latestItems.length
      ) {
        toUpdate.push({
          ...parentArticle,
          groupId: parentArticle.groupId ?? "",
          groupName: parentArticle.groupName ?? "",
          articlesCountInCombination: latestItems.length,
        });
      }
    }
  }

  return { toAdd, toUpdate, toDelete };
}

export async function enrichToAddArticles(
  toAdd: SyncArticle[]
): Promise<Partial<Article>[]> {
  if (toAdd.length === 0) return [];

  const itemsInfo = await fetchItemsInfo(
    toAdd.map((item) => item.itemNo).join(",")
  );
  const itemsInfoByNo = new Map<string, Item>();
  for (const info of itemsInfo) {
    if (!itemsInfoByNo.has(info.no)) itemsInfoByNo.set(info.no, info);
  }

  return toAdd.flatMap((addItem) => {
    const info = itemsInfoByNo.get(addItem.itemNo);
    if (!info) return [];

    return [
      {
        productArticleId: info.no,
        productArticleType: info.type,
        hfb: info.hfbNo || "Unknown",
        itemName: info.name || "Unknown",
        itemType: info.description ?? "Unknown",
        itemColor: info.description?.split(",")?.[1] || "Unknown",
        imageSmall: info.smallImageUrl || info.imageUrl || "Unknown",
        parentArticleCombinationNumber: addItem.parentId,
        nbrArticles: addItem.parentQty || 1,
        groupId: addItem.groupId,
        groupName: addItem.groupName ?? "",
        mediaId: addItem.mediaId || "",
        articlesCountInCombination: addItem.quantity,
        active: "ACTIVE",
      },
    ];
  });
}
